import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import { useMessage } from '../common/MessageDisplay';

const UpdateProfilePage = () => {
  const { ErrorDisplay, SuccessDisplay, showError, showSuccess } = useMessage();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [user, setUser] = useState({
    name: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({
    name: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await ApiService.getAccountDetails();
      setUser((prev) => ({
        ...prev,
        name: response?.data?.name ?? '',
        phoneNumber: response?.data?.phoneNumber ?? '',
      }));
    } catch (error) {
      showError(error?.response?.data?.message || error?.message || 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const nextErrors = {
      name: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
    };

    if (!user.name.trim()) {
      nextErrors.name = 'Name is required';
    }

    if (user.password && user.password.length < 4) {
      nextErrors.password = 'Password must be at least 4 characters long';
    }

    if (user.password !== user.confirmPassword) {
      // si ambos están vacíos, esto da true (no hay error). Si uno tiene algo, sí valida.
      if (user.password || user.confirmPassword) {
        nextErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(nextErrors);

    // Mostrar el primer error por toast
    const firstError = Object.values(nextErrors).find((msg) => msg);
    if (firstError) showError(firstError);

    return !firstError; // true = válido
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setUser((prev) => ({
      ...prev,
      [name]: value,
    }));

    // limpia el error del campo mientras escribe
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const isToUpdate = window.confirm('Are you sure you want to update your profile?');
    if (!isToUpdate) return;

    try {
      setSaving(true);

      const requestBody = {
        name: user.name.trim(),
        phoneNumber: user.phoneNumber?.trim() ? user.phoneNumber.trim() : null,
        ...(user.password ? { password: user.password } : {}),
      };

      const resp = await ApiService.updateMyAccount(requestBody);

      if (resp?.statusCode === 200) {
        showSuccess('Profile updated successfully');

        // 👇 espera para que el usuario vea el mensaje
        setTimeout(() => {
          navigate('/profile', { replace: true });
        }, 800);

        return;
      }

      showError(resp?.message || 'Unexpected response from server.');
    } catch (error) {
      showError(error?.response?.data?.message || error?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="update-profile-loading">Loading Profile...</div>;

  return (
    <div className="update-profile-container">
      <div className="update-profile-card">
        <ErrorDisplay />
        <SuccessDisplay />

        <h2 className="update-profile-title">Update Profile</h2>

        <form onSubmit={handleSubmit} className="update-profile-form">
          <div className="update-profile-form-group">
            <label htmlFor="name" className="update-profile-label">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={user.name}
              onChange={handleChange}
              className={`update-profile-input ${errors.name ? 'error' : ''}`}
            />
            {errors.name && <span className="update-profile-error">{errors.name}</span>}
          </div>

          <div className="update-profile-form-group">
            <label htmlFor="phoneNumber" className="update-profile-label">
              Phone Number
            </label>
            <input
              type="text"
              id="phoneNumber"
              name="phoneNumber"
              value={user.phoneNumber}
              onChange={handleChange}
              className={`update-profile-input ${errors.phoneNumber ? 'error' : ''}`}
              placeholder="Optional"
            />
            {errors.phoneNumber && (
              <span className="update-profile-error">{errors.phoneNumber}</span>
            )}
          </div>

          <div className="update-profile-form-group">
            <label htmlFor="password" className="update-profile-label">
              New Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={user.password}
              onChange={handleChange}
              className={`update-profile-input ${errors.password ? 'error' : ''}`}
              placeholder="Leave blank to keep current password"
            />
            {errors.password && <span className="update-profile-error">{errors.password}</span>}
          </div>

          <div className="update-profile-form-group">
            <label htmlFor="confirmPassword" className="update-profile-label">
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={user.confirmPassword}
              onChange={handleChange}
              className={`update-profile-input ${errors.confirmPassword ? 'error' : ''}`}
              placeholder="Leave blank to keep current password"
            />
            {errors.confirmPassword && (
              <span className="update-profile-error">{errors.confirmPassword}</span>
            )}
          </div>

          <div className="update-profile-actions">
            <button type="submit" className="update-profile-submit">
              Update Profile
            </button>
            <Link to="/profile" className="update-profile-cancel">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateProfilePage;
